import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RoutineTimelineView, TimelineRouteItem } from '../../../../types/RoutineTimeline.types';
import {
  ExpectedAvailability,
  ExpectedDescription,
  ExpectedIcon,
  ExpectedSegment,
  ExpectedSegments,
  ExpectedTitle,
  ExpectedUnitGroup,
  ExpectedValue,
  ExpectedValueRow,
  FadeBottom,
  FadeTop,
  Header,
  ScheduleTitle,
  ScrollArea,
  TimelineList,
  TotalProgress,
  Wrapper,
} from './RoutineTimeline.styles';
import { TimelineItem } from './TimelineItem';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import type { Optional } from 'lib/types/Optional';

interface RoutineTimelineProps {
  data: RoutineTimelineView;
  isPaused?: boolean;
  showSkip?: boolean;
  isSkipDisabled?: boolean;
  isSkipPending?: boolean;
  onSkipRoute?: () => void;
  onDownloadReport?: (item: TimelineRouteItem) => void;
  exportingReportId?: Optional<string>;
}

export const RoutineTimeline = ({
  data,
  isPaused = false,
  showSkip = true,
  isSkipDisabled = false,
  isSkipPending = false,
  onSkipRoute,
  onDownloadReport,
  exportingReportId,
}: RoutineTimelineProps): JSX.Element => {
  const { t } = useTranslation();
  const { cleaningCompletion, fullRecharge } = data.expectedAvailability;
  const hasExpectedAvailability = !!cleaningCompletion || !!fullRecharge;
  const listRef = useRef<HTMLDivElement>(null);
  const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  // Fade an edge only while there is more content to scroll to in that direction.
  const updateFades = useCallback((): void => {
    const list = listRef.current;

    if (!list) {
      return;
    }

    setIsScrolledFromTop(list.scrollTop > 0);
    setHasMoreBelow(list.scrollTop + list.clientHeight < list.scrollHeight - 1);
  }, []);

  // Re-measure whenever the list or any of its items change size (modal open animation, fonts,
  // expected-availability block), not only on data or window changes.
  useEffect(() => {
    const list = listRef.current;

    updateFades();

    if (!list || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(updateFades);
    observer.observe(list);
    Array.from(list.children).forEach(child => observer.observe(child));

    return (): void => observer.disconnect();
  }, [updateFades, data.timeline]);

  return (
    <Wrapper>
      <Header>
        <ScheduleTitle>{data.scheduleName}</ScheduleTitle>
        <TotalProgress>
          {t('robotCleaningControl.liveMap.totalProgress', {
            completed: data.completedRoutes,
            total: data.totalRoutes,
          })}
        </TotalProgress>
      </Header>

      <ScrollArea>
        <FadeTop $isVisible={isScrolledFromTop} />
        <TimelineList ref={listRef} onScroll={updateFades}>
          {data.timeline.map((item, index) => (
            <TimelineItem
              key={item.id}
              item={item}
              isLast={index === data.timeline.length - 1}
              isPaused={isPaused}
              showSkip={showSkip}
              isSkipDisabled={isSkipDisabled}
              isSkipPending={isSkipPending}
              onSkipRoute={onSkipRoute}
              onDownloadReport={onDownloadReport}
              exportingReportId={exportingReportId}
            />
          ))}

          {hasExpectedAvailability && (
            <ExpectedAvailability>
              <ExpectedTitle>{t('robotCleaningControl.liveMap.expectedAvailability')}</ExpectedTitle>
              <ExpectedSegments>
                {cleaningCompletion && (
                  <ExpectedSegment>
                    <ExpectedValueRow>
                      <ExpectedIcon>
                        <SvgIcon name="cleanStar" size={24} />
                      </ExpectedIcon>
                      <ExpectedValue>
                        <ExpectedUnitGroup>
                          <span>{cleaningCompletion.hours}</span>
                          <span>{t('robotCleaningControl.liveMap.hoursUnit')}</span>
                        </ExpectedUnitGroup>
                        <ExpectedUnitGroup>
                          <span>{cleaningCompletion.minutes}</span>
                          <span>{t('robotCleaningControl.liveMap.minutesUnit')}</span>
                        </ExpectedUnitGroup>
                      </ExpectedValue>
                    </ExpectedValueRow>
                    <ExpectedDescription>{t('robotCleaningControl.liveMap.cleaningCompletion')}</ExpectedDescription>
                  </ExpectedSegment>
                )}

                {fullRecharge && (
                  <ExpectedSegment>
                    <ExpectedValueRow>
                      <ExpectedIcon>
                        <SvgIcon name="batteryPercentage" size={24} />
                      </ExpectedIcon>
                      <ExpectedValue>
                        <ExpectedUnitGroup>
                          <span>{fullRecharge.hours}</span>
                          <span>{t('robotCleaningControl.liveMap.hoursUnit')}</span>
                        </ExpectedUnitGroup>
                        <ExpectedUnitGroup>
                          <span>{fullRecharge.minutes}</span>
                          <span>{t('robotCleaningControl.liveMap.minutesUnit')}</span>
                        </ExpectedUnitGroup>
                      </ExpectedValue>
                    </ExpectedValueRow>
                    <ExpectedDescription>{t('robotCleaningControl.liveMap.fullRecharge')}</ExpectedDescription>
                  </ExpectedSegment>
                )}
              </ExpectedSegments>
            </ExpectedAvailability>
          )}
        </TimelineList>
        <FadeBottom $isVisible={hasMoreBelow} />
      </ScrollArea>
    </Wrapper>
  );
};
